import omit from 'lodash-es/omit';
import omitBy from 'lodash-es/omitBy';
import pick from 'lodash-es/pick';

import type { JaneSearchState } from '@jane/search/data-access';
import {
  INSTANTSEARCH_STATE_KEYS,
  JANE_SEARCH_STATE_KEYS,
  encodeQuery,
  parseSearch,
} from '@jane/shared/util';

type QueryParams = Record<string, any>;

/**
 * Parses the initial location query and returns the search state
 *
 * - Removes any non-search params
 * - Removes the default sort or any incomplete sort params
 */
export const searchStateFromLocationQuery = (locationSearch: string) => {
  const searchParams = parseSearch(locationSearch);

  deleteDefaultOrIncompleteCurrentSort(searchParams);

  return searchParams;
};

/**
 * Get the current search state, along with any other
 * non-search params from the location.
 *
 * - Removes `page` param from the query
 * - Removes any instantsearch state from the query, as we will
 *   have mapped that state to the new search state.
 * - Filters out any null/undefined values
 */
export function getLocationQueryWithSearchState<T>(
  locationSearch: string,
  searchState: JaneSearchState<T>
) {
  const currentParams = parseSearch(locationSearch);
  const allParams = {
    ...currentParams,
    ...searchState,
  };
  const filteredParams = omitBy(
    omit(allParams, ['page', ...INSTANTSEARCH_STATE_KEYS]),
    (value) => !value
  );

  return encodeQuery('', filteredParams);
}

export const getSearchParams = <T>({
  ...searchState
}: QueryParams): JaneSearchState<T> =>
  pick(
    omitBy(searchState, (value) => !value),
    JANE_SEARCH_STATE_KEYS
  );

export const deleteDefaultOrIncompleteCurrentSort = (
  searchParams: QueryParams
): void => {
  const { currentSort } = searchParams;
  if (currentSort) {
    if (!('id' in currentSort) || !('suffix' in currentSort)) {
      delete searchParams['currentSort'];
    }
    if (currentSort.isDefault !== undefined) {
      delete searchParams['currentSort'];
    }
  }
};
